import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import { getCloudfrontUrl } from '@/utils/cloudfront';
import { Button, Stack, Typography, Tooltip, CircularProgress, Box, Skeleton } from '@mui/material';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useFeatureFlag, useRouter } from '@/hooks';
import { useAtomValue } from 'jotai';
import * as state from '@/components/Analysis/state';
import { LocationTag, VIEW_IN_3D_TOOLTIP_LABEL } from '@/constants';
import {
  AttachmentActionContainer,
  AttachmentContainer,
  iconStyle,
  PdfContainer,
  PdfItem,
  textStyle,
} from './styles';
import { Attachment } from '@/components/shared/AttachmentDisplay/type';
import UploadFileDialog from '@/components/Analysis/shared/MediaUploadsAndViewer/UploadFileDialog';
import { urlToBase64 } from '@/utils';
import {
  FileTypes,
  FileUploadStatus,
  ImageType,
  useDeleteFilesMutation,
  useGetAssemblyAttachmentsQuery,
} from '@/__generated__/graphql';
import CloseIcon from '@mui/icons-material/Close';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { ConfirmationDialog } from '@/components/shared/ConfirmationDialog';
import AssemblyAttachmentCaption from '../AssemblyAttachmentCaption/AssemblyAttachmentCaption';

type ActionButtonsProps = {
  isUpdating?: boolean;
  onView3D: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onUploadImages: () => void;
};

const ActionButtons = ({ isUpdating, onView3D, onUploadImages }: ActionButtonsProps) => (
  <Stack direction="row" gap={2}>
    <Tooltip title={VIEW_IN_3D_TOOLTIP_LABEL} placement="bottom" arrow>
      <Button
        variant="outlined"
        size="small"
        startIcon={<AbyssIcon iconName="cube-secondary" size="md" />}
        onClick={onView3D}
      >
        Add 3D View
      </Button>
    </Tooltip>
    <Button
      variant="outlined"
      size="small"
      startIcon={
        isUpdating ? <CircularProgress size={12} /> : <AbyssIcon iconName="media" size="md" />
      }
      onClick={onUploadImages}
      id={`${LocationTag.ReviewPanel}-upload-images-button`}
    >
      {isUpdating ? 'Uploading' : 'Upload Images'}
    </Button>
  </Stack>
);

const InfoPanel = ({ text, textSize }: { text: string; textSize: string }) => (
  <>
    <AbyssIcon iconName="attach-media" size="2xl" />
    <Typography sx={{ ...textStyle, fontSize: textSize }}>{text}</Typography>
  </>
);

export const AttachmentsSection = ({ assemblyId }: { assemblyId?: string }) => {
  const router = useRouter();
  const structureId = useGetStructureId();
  const [deleteFiles] = useDeleteFilesMutation();
  const selectedAssemblyName = useAtomValue(state.selectedAssemblyName);
  const selectedAssemblyId = useAtomValue(state.selectedAssemblyId);
  const [openFileDialog, setOpenFileDialog] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [imageToDelete, setImageToDelete] = useState<string | undefined>(undefined);
  const showPdf = useFeatureFlag(['ait-engineer', 'api-inspector']);
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });
  const { data } = useGetAssemblyAttachmentsQuery({
    variables: { assemblyId: assemblyId || selectedAssemblyId },
  });

  useEffect(() => {
    const mapAssemblyAttachments = async () => {
      setLoading(true);
      const files = data?.assembly?.files || [];
      const imageFiles = files.filter(
        ({ id, uploadStatus, imageType }) =>
          id &&
          uploadStatus === FileUploadStatus.Success &&
          (!imageType || imageType !== ImageType.WhiteBgPdf)
      );
      const mappedAttachments = await Promise.all(
        imageFiles.map(async (file) => {
          let imageBase64 = '';
          if (file.resourcePath) {
            imageBase64 = await urlToBase64(getCloudfrontUrl(file.resourcePath));
          }
          return {
            id: file.id!,
            name: file.name,
            fileName: file.fileName,
            type: file.type,
            url: getCloudfrontUrl(file.resourcePath),
            caption: file.caption ?? undefined,
            isNew: false,
            resourcePath: file.resourcePath,
            base64: imageBase64,
            imageType: file.imageType || undefined,
            uuid: file.uuid || undefined,
          };
        })
      );
      setAttachments(mappedAttachments);
      setLoading(false);
    };
    if (data) mapAssemblyAttachments();
  }, [data]);

  const handleViewIn3D = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      const url = `/analysis/${structureId}/viewer?assembly=${selectedAssemblyName}`;
      const isCtrlClick = event.ctrlKey || event.metaKey;

      isCtrlClick ? window.open(url, '_blank') : router.push(url);
    },
    [router, structureId, selectedAssemblyName]
  );

  const handleUploadImages = useCallback(() => {
    setOpenFileDialog(true);
  }, []);

  const { images, pdfs } = useMemo(() => {
    return {
      images: attachments.filter((a) =>
        [FileTypes.Png, FileTypes.Jpeg].includes(a?.type as FileTypes)
      ),
      pdfs: attachments?.filter((a) => a?.type === FileTypes.Pdf),
    };
  }, [attachments]);

  const handleRemoveImage = useCallback(
    async (id: string | undefined) => {
      if (!id) return;
      const currentAttachment = data?.assembly?.files?.find(
        (eachAttachment) => eachAttachment.id === id
      );
      if (!currentAttachment) return;
      const attachmentsToRemove: string[] = [];
      attachmentsToRemove.push(id);
      const otherAttachmentsToRemove = data?.assembly?.files?.find(
        (eachAttachment) =>
          eachAttachment.id !== id &&
          eachAttachment.uuid === `${currentAttachment.uuid}-${ImageType.WhiteBgPdf}` &&
          eachAttachment.imageType === ImageType.WhiteBgPdf
      );
      if (otherAttachmentsToRemove?.id) attachmentsToRemove.push(otherAttachmentsToRemove.id);
      setIsUpdating(true);
      setAttachments((prev) => prev.filter((img) => img.id !== id));
      await deleteFiles({
        variables: {
          input: {
            referenceId: selectedAssemblyId ?? assemblyId ?? '',
            collectionName: 'assembly',
            fileIds: attachmentsToRemove,
          },
        },
        onCompleted: () => {
          setIsUpdating(false);
          showSnackBar('Attachments have been successfully updated', 'success');
        },
        onError: (error) => {
          setIsUpdating(false);
          showSnackBar(error.message, 'error');
        },
      });
    },
    [assemblyId, data?.assembly?.files, deleteFiles, selectedAssemblyId, showSnackBar]
  );

  const openDeleteDialog = useCallback((id: string) => {
    setImageToDelete(id);
    setConfirmDialogOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (imageToDelete) {
      handleRemoveImage(imageToDelete);
    }
    setConfirmDialogOpen(false);
    setImageToDelete(undefined);
  }, [imageToDelete, handleRemoveImage]);

  const handleCancelDelete = useCallback(() => {
    setConfirmDialogOpen(false);
  }, []);

  return (
    <AttachmentContainer gap={2} id={`${LocationTag.ReviewPanelComponentOverview}-attachments`}>
      {loading ? (
        <Stack direction="column" alignItems="center" p={2} gap={3} sx={{ width: '100%' }}>
          {[...Array(3)].map((_, idx) => (
            <React.Fragment key={idx}>
              <Skeleton variant="rectangular" width="100%" height={400} />
              <Skeleton variant="text" width={180} height={24} />
            </React.Fragment>
          ))}
        </Stack>
      ) : attachments?.length > 0 ? (
        <Stack direction="column" p={2} sx={{ width: '100%' }}>
          <Box sx={{ width: '100%' }}>
            {images.map(({ base64, ...selectedAttachment }) => (
              <React.Fragment key={selectedAttachment.id}>
                <Box
                  sx={{ position: 'relative', width: '100%' }}
                  key={selectedAttachment.id}
                  className={`${LocationTag.ReviewPanelComponentOverview}-image-attachment`}
                >
                  <CloseIcon
                    onClick={() => openDeleteDialog(selectedAttachment.id!)}
                    sx={iconStyle}
                  />
                  <img
                    src={base64 || selectedAttachment.url}
                    alt={selectedAttachment.fileName}
                    style={{ width: '100%' }}
                  />
                </Box>
                <AssemblyAttachmentCaption selectedAttachment={selectedAttachment} />
              </React.Fragment>
            ))}
          </Box>

          {showPdf && (
            <PdfContainer>
              {pdfs.map(({ ...selectedAttachment }) => (
                <Box
                  sx={{ position: 'relative', width: '100%' }}
                  key={selectedAttachment.id}
                  className={`${LocationTag.ReviewPanelComponentOverview}-pdf-attachment`}
                >
                  <CloseIcon
                    onClick={() => openDeleteDialog(selectedAttachment.id!)}
                    sx={iconStyle}
                  />
                  <a
                    href={selectedAttachment.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'none' }}
                  >
                    <PdfItem>
                      <img src="/assets/pdf-assets/pdf.png" width={50} height={50} />
                    </PdfItem>
                  </a>
                  <AssemblyAttachmentCaption selectedAttachment={selectedAttachment} />
                </Box>
              ))}
            </PdfContainer>
          )}

          <AttachmentActionContainer>
            <Stack direction="row" alignItems="center" gap={2}>
              <InfoPanel
                textSize="1.2rem"
                text="You can either capture a screenshot from the 3D view or upload an image or file here"
              />
            </Stack>
            <ActionButtons
              isUpdating={isUpdating}
              onView3D={handleViewIn3D}
              onUploadImages={handleUploadImages}
            />
          </AttachmentActionContainer>
        </Stack>
      ) : (
        <>
          <InfoPanel
            textSize="1.2rem"
            text="You can either take a screenshot from the 3D view or upload an image here with the POI locations"
          />
          <ActionButtons
            isUpdating={isUpdating}
            onView3D={handleViewIn3D}
            onUploadImages={handleUploadImages}
          />
        </>
      )}
      <UploadFileDialog
        openDialog={openFileDialog}
        setOpenDialog={setOpenFileDialog}
        referenceId={selectedAssemblyId ?? ''}
        collectionName="assembly"
        attachments={attachments}
        setAttachments={setAttachments}
        setIsUpdating={setIsUpdating}
        dialogTitle="Attachments"
        buttonText="Add"
        enableUpload={true}
        location={LocationTag.ReviewPanelComponentOverview}
      />
      <ConfirmationDialog
        isOpen={confirmDialogOpen}
        title="Delete Attachment"
        description={`Are you sure you want to delete "${
          attachments.find((a) => a.id === imageToDelete)?.caption ||
          attachments.find((a) => a.id === imageToDelete)?.name
        }"?`}
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        handleClose={handleCancelDelete}
        loading={{ state: false, text: '' }}
      />
    </AttachmentContainer>
  );
};
